import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';
import GuestBanner from '../components/GuestBanner';
import GuestAccessibleBadge from '../components/GuestAccessibleBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  BookOpen,
  GraduationCap,
  FlaskConical,
  ExternalLink,
  Plus,
  Loader2,
  Search,
  FileText,
  Users,
  MapPin,
  Clock,
  Send,
  Microscope,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'all', label: 'All Topics' },
  { value: 'behavior', label: 'Behavior' },
  { value: 'health', label: 'Health' },
  { value: 'genetics', label: 'Genetics' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'cognition', label: 'Cognition' },
  { value: 'training', label: 'Training Science' },
  { value: 'general', label: 'General' },
];

const CATEGORY_COLORS = {
  behavior: 'bg-purple-100 text-purple-800',
  health: 'bg-red-100 text-red-800',
  genetics: 'bg-blue-100 text-blue-800',
  nutrition: 'bg-green-100 text-green-800',
  cognition: 'bg-amber-100 text-amber-800',
  training: 'bg-teal-100 text-teal-800',
  general: 'bg-gray-100 text-gray-800',
};

const Research = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('popular');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newArticle, setNewArticle] = useState({
    title: '', url: '', summary: '', source_name: '',
    article_type: 'popular', category: 'general',
  });

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { article_type: activeTab };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const res = await axios.get(`${API}/research/articles`, { params });
      setArticles(res.data.articles || []);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, categoryFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/research/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
    fetchStats();
  }, [fetchArticles, fetchStats]);

  const handleSubmit = async () => {
    if (!newArticle.title.trim() || !newArticle.url.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/research/articles/submit`, newArticle);
      toast.success('Article submitted for review!');
      setSubmitOpen(false);
      setNewArticle({ title: '', url: '', summary: '', source_name: '', article_type: 'popular', category: 'general' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = searchQuery
    ? articles.filter(a =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.summary?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.source_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : articles;

  const PopularCard = ({ article }) => (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
      data-testid={`article-${article.id}`}
    >
      <Card className="h-full hover:shadow-md transition-shadow overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-start gap-3 mb-3">
            <Badge className={`${CATEGORY_COLORS[article.category] || CATEGORY_COLORS.general} text-xs shrink-0`}>
              {article.category}
            </Badge>
            <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
          </div>
          <h3 className="font-heading font-semibold text-base mb-2 group-hover:text-amber-700 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{article.summary}</p>
          <p className="text-xs text-muted-foreground font-medium">{article.source_name}</p>
        </CardContent>
      </Card>
    </a>
  );

  const AcademicCard = ({ article }) => (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block"
      data-testid={`article-${article.id}`}
    >
      <Card className="h-full hover:shadow-md transition-shadow">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`${CATEGORY_COLORS[article.category] || CATEGORY_COLORS.general} text-xs`}>
              {article.category}
            </Badge>
            {article.is_open_access && (
              <Badge className="bg-green-100 text-green-800 text-xs">Open Access</Badge>
            )}
            {article.source_name?.includes('arXiv') && (
              <Badge variant="outline" className="text-xs">Preprint</Badge>
            )}
          </div>
          <h3 className="font-heading font-semibold text-sm mb-2 group-hover:text-amber-700 transition-colors line-clamp-2">
            {article.title}
          </h3>
          {article.authors && (
            <p className="text-xs text-muted-foreground mb-2 line-clamp-1">{article.authors}</p>
          )}
          <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{article.summary}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileText className="w-3 h-3" />
            <span>{article.journal || article.source_name}</span>
            {article.doi && <span className="truncate">DOI: {article.doi}</span>}
          </div>
        </CardContent>
      </Card>
    </a>
  );

  const OpportunityCard = ({ article }) => (
    <Card className="h-full" data-testid={`article-${article.id}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Badge className={`${CATEGORY_COLORS[article.category] || CATEGORY_COLORS.general} text-xs`}>
            {article.category}
          </Badge>
          <Badge variant="outline" className="text-xs">Recruiting</Badge>
        </div>
        <h3 className="font-heading font-semibold text-base mb-2">{article.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{article.summary}</p>
        <div className="space-y-1 text-xs text-muted-foreground mb-4">
          {article.institution && (
            <p className="flex items-center gap-1"><GraduationCap className="w-3 h-3" /> {article.institution}</p>
          )}
          {article.eligibility && (
            <p className="flex items-center gap-1"><Users className="w-3 h-3" /> {article.eligibility}</p>
          )}
          {article.location && (
            <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {article.location}</p>
          )}
          {article.deadline && (
            <p className="flex items-center gap-1"><Clock className="w-3 h-3" /> Deadline: {article.deadline}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" asChild className="flex-1">
            <a href={article.contact_url || article.url} target="_blank" rel="noopener noreferrer">
              <Send className="w-3 h-3 mr-1" /> Participate
            </a>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={article.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-3 h-3 mr-1" /> Details
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Dog Science"
        description="Explore the latest dog science — popular articles, academic papers, and opportunities to participate in canine research studies."
        url="/research"
      />

      {/* Hero */}
      <div className="bg-gradient-to-r from-indigo-700 to-indigo-600 text-white py-10">
        <div className="container mx-auto px-4">
          <div className="flex items-start gap-3 mb-3 justify-between flex-wrap">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl sm:text-4xl font-heading font-medium flex items-center gap-3">
                <Microscope className="w-9 h-9" />
                Dog Science
              </h1>
              <GuestAccessibleBadge className="bg-white/20 text-white" />
            </div>
            <Link
              to="/suggest-content?type=research"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 text-sm font-medium border border-white/30 transition"
              data-testid="suggest-research-source-btn"
            >
              Suggest a Source →
            </Link>
          </div>
          <p className="text-indigo-200 text-lg max-w-2xl">
            Discover the latest science about our canine companions — from popular articles
            to peer-reviewed papers and opportunities to contribute to real studies.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <GuestBanner message="Sign up to submit articles and contribute to the research collection" />

        {/* Stats + Controls */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex gap-4 text-sm">
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4 text-amber-600" />
              <strong>{stats.popular || 0}</strong> Popular Articles
            </span>
            <span className="flex items-center gap-1.5">
              <GraduationCap className="w-4 h-4 text-indigo-600" />
              <strong>{stats.academic || 0}</strong> Academic Papers
            </span>
            <span className="flex items-center gap-1.5">
              <FlaskConical className="w-4 h-4 text-green-600" />
              <strong>{stats.opportunities || 0}</strong> Opportunities
            </span>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-56"
                data-testid="research-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-40" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {user && (
              <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="submit-article-btn">
                    <Plus className="w-4 h-4 mr-1" /> Submit Article
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Submit a Research Article</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <Input
                      placeholder="Article title *"
                      value={newArticle.title}
                      onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                      data-testid="submit-title"
                    />
                    <Input
                      placeholder="URL *"
                      value={newArticle.url}
                      onChange={(e) => setNewArticle({ ...newArticle, url: e.target.value })}
                      data-testid="submit-url"
                    />
                    <Input
                      placeholder="Source name (e.g., Nature, AKC)"
                      value={newArticle.source_name}
                      onChange={(e) => setNewArticle({ ...newArticle, source_name: e.target.value })}
                    />
                    <Textarea
                      placeholder="Brief summary — why is this interesting?"
                      value={newArticle.summary}
                      onChange={(e) => setNewArticle({ ...newArticle, summary: e.target.value })}
                      rows={3}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Select value={newArticle.article_type} onValueChange={(v) => setNewArticle({ ...newArticle, article_type: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="popular">Popular Science</SelectItem>
                          <SelectItem value="academic">Academic Paper</SelectItem>
                          <SelectItem value="opportunity">Research Opportunity</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={newArticle.category} onValueChange={(v) => setNewArticle({ ...newArticle, category: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.filter(c => c.value !== 'all').map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setSubmitOpen(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-article-confirm">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                      Submit for Review
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList data-testid="research-tabs">
            <TabsTrigger value="popular" data-testid="tab-popular">
              <BookOpen className="w-4 h-4 mr-1.5" /> Popular Science
            </TabsTrigger>
            <TabsTrigger value="academic" data-testid="tab-academic">
              <GraduationCap className="w-4 h-4 mr-1.5" /> Academic Papers
            </TabsTrigger>
            <TabsTrigger value="opportunity" data-testid="tab-opportunity">
              <FlaskConical className="w-4 h-4 mr-1.5" /> Participate
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : filtered.length === 0 ? (
            <Card className="text-center py-16">
              <CardContent>
                <Microscope className="w-14 h-14 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-xl mb-2">No articles found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? 'Try a different search term' : 'Check back soon for new content!'}
                </p>
                {user && (
                  <Button onClick={() => setSubmitOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Submit an Article
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <TabsContent value="popular">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filtered.map(a => <PopularCard key={a.id} article={a} />)}
                </div>
              </TabsContent>

              <TabsContent value="academic">
                <div className="grid md:grid-cols-2 gap-5">
                  {filtered.map(a => <AcademicCard key={a.id} article={a} />)}
                </div>
              </TabsContent>

              <TabsContent value="opportunity">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filtered.map(a => <OpportunityCard key={a.id} article={a} />)}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Research;
